import { useState } from "react";
import { X, ArrowRight } from "lucide-react";
import { GoogleLogin } from "@react-oauth/google";
import Wordmark from "./Wordmark";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";

export default function AuthModal({ onSuccess, onDismiss }) {
  const [tab, setTab] = useState("register"); // "register" | "login"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleGoogleSuccess = async (credentialResponse) => {
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/google`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ credential: credentialResponse.credential }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || "Google sign-in failed. Please try again.");
        return;
      }
      localStorage.setItem("yousift_token", data.token);
      localStorage.setItem("yousift_user_id", data.user_id);
      localStorage.setItem("yousift_email", data.email);
      onSuccess({
        token: data.token,
        user_id: data.user_id,
        email: data.email,
        quiz_completed: data.quiz_completed,
      });
    } catch {
      setError("We couldn't reach the server. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const endpoint = tab === "register" ? "/api/auth/register" : "/api/auth/login";

    try {
      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.detail || "Something went wrong. Please try again.");
        return;
      }

      localStorage.setItem("yousift_token", data.token);
      localStorage.setItem("yousift_user_id", data.user_id);
      localStorage.setItem("yousift_email", data.email);

      onSuccess({
        token: data.token,
        user_id: data.user_id,
        email: data.email,
        quiz_completed: data.quiz_completed,
      });
    } catch {
      setError("We couldn't reach the server. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/80 backdrop-blur-sm">
      <div className="relative w-full max-w-md bg-[#141414] border border-[#2A2A2A] rounded-2xl p-8 shadow-2xl">
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="absolute top-4 right-4 text-gray-600 hover:text-gray-400 transition-colors"
          >
            <X size={20} />
          </button>
        )}

        <div className="text-center mb-8">
          <Wordmark size="sm" darkBg={true} />
          <p className="text-gray-400 text-sm mt-2">
            {tab === "register"
              ? "Create your free account to see your results."
              : "Welcome back. Sign in to continue."}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex bg-[#1A1A1A] rounded-xl p-1 mb-6">
          {["register", "login"].map((t) => (
            <button
              key={t}
              onClick={() => { setTab(t); setError(""); }}
              className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${
                tab === t
                  ? "bg-[#FF0000] text-white"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              {t === "register" ? "Create Account" : "Sign In"}
            </button>
          ))}
        </div>

        {/* Google Sign-In */}
        {process.env.REACT_APP_GOOGLE_CLIENT_ID && (
          <>
            <div className="flex justify-center mb-4">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => setError("Google sign-in failed. Please try again.")}
                theme="filled_black"
                shape="rectangular"
                width="368"
              />
            </div>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-[#2A2A2A]" />
              <span className="text-gray-600 text-xs uppercase tracking-widest">or</span>
              <div className="flex-1 h-px bg-[#2A2A2A]" />
            </div>
          </>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-widest mb-1.5 block">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              className="w-full px-4 py-3 bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-[#FF0000] transition-all"
            />
          </div>

          <div>
            <label className="text-xs text-gray-500 uppercase tracking-widest mb-1.5 block">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={tab === "register" ? "At least 8 characters" : "Your password"}
              required
              minLength={tab === "register" ? 8 : 1}
              className="w-full px-4 py-3 bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-[#FF0000] transition-all"
            />
          </div>

          {error && (
            <p className="text-red-400 text-sm">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-[#FF0000] hover:bg-[#CC0000] disabled:opacity-50 text-white font-black rounded-xl transition-all duration-200 flex items-center justify-center gap-2 mt-2"
          >
            {loading ? (
              <Spinner />
            ) : (
              <>
                {tab === "register" ? "Create Account" : "Sign In"}
                <ArrowRight size={18} />
              </>
            )}
          </button>
        </form>

        <p className="text-center text-gray-600 text-xs mt-6">
          By continuing, you agree to our{" "}
          <a href="/terms" className="text-gray-400 hover:text-white underline transition-colors">Terms of Use</a>
          {" "}and{" "}
          <a href="/privacy" className="text-gray-400 hover:text-white underline transition-colors">Privacy Policy</a>.
        </p>
      </div>
    </div>
  );
}

function Spinner() {
  return (
    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
    </svg>
  );
}
